// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

export type LegalPanel = "privacy" | "terms" | "imprint"

/** Public URLs of the legal pages. German paths are the canonical ones (Germany-first); the page content follows the reader's locale. */
export const LEGAL_PATHS: Record<LegalPanel, string> = {
  privacy: "/datenschutz",
  terms: "/bedingungen",
  imprint: "/impressum",
}

export const LEGAL_TITLE_KEYS: Record<LegalPanel, string> = {
  privacy: "landingPage.legal.privacyTitle",
  terms: "landingPage.legal.termsTitle",
  imprint: "landingPage.legal.imprintTitle",
}

/** The browser-tab title names the document (the page heading can be just the brand, as on the imprint). */
export const LEGAL_DOCUMENT_TITLE_KEYS: Record<LegalPanel, string> = {
  privacy: "landingPage.footer.privacyLink",
  terms: "landingPage.footer.termsLink",
  imprint: "landingPage.footer.imprintLink",
}

/**
 * Build output of scripts/prerender-legal.mjs: each page's full text as static HTML,
 * readable without JavaScript. Amplify rewrites each path to its file (infra-cdk
 * amplify-hosting-construct.ts) ahead of the SPA catch-all.
 */
export const LEGAL_STATIC_FILES: Record<LegalPanel, string> = {
  privacy: "datenschutz.html",
  terms: "bedingungen.html",
  imprint: "impressum.html",
}

/** The pre-2026-09-29 paths, still linked from sign-up consent records, docs and earlier builds — each redirects to its canonical path. */
export const LEGACY_LEGAL_PANELS: ReadonlySet<string> = new Set<LegalPanel>([
  "privacy",
  "terms",
  "imprint",
])

/**
 * The contact form's own URL — the Impressum's second contact channel. Renders
 * the landing page scrolled to the form, for signed-in visitors too (they are
 * otherwise sent from "/" straight into the app).
 */
export const CONTACT_PATH = "/kontakt"
